import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Workflow } from '../common/review-types';
import { ApiRoutesV1 } from '../common/routes';
import { AjaxData, AjaxService, blankHttpParamsWithCustomEncoder, formUrlEncodedHttpOptions } from './ajax.service';

export interface EmailPreviewSettings {
  requestTemplateId: string;
  reviewGenType: string;
}

@Injectable()
export class ReviewGenSettingsService {
  private defaultTemplateId$$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(private readonly ajaxService: AjaxService) {}

  public getEmailPreviewSettings(): Observable<EmailPreviewSettings> {
    return this.ajaxService.get(ApiRoutesV1.reviewGenerationSettingsGet).pipe(
      map((response: HttpResponse<AjaxData>) => {
        return <EmailPreviewSettings>{
          requestTemplateId: response.body.data.requestTemplateId,
          reviewGenType: response.body.data.reviewGenType,
        };
      }),
    );
  }

  public getReviewGenType(): Observable<Workflow> {
    return this.ajaxService.get(ApiRoutesV1.reviewGenerationSettingsGet).pipe(
      map((response: HttpResponse<AjaxData>) => {
        if (response.body.data.reviewGenType === Workflow.Internal.toString()) {
          return Workflow.Internal;
        } else if (response.body.data.reviewGenType === Workflow.External.toString()) {
          return Workflow.External;
        }
        throw new Error(`Workflow '${response.body.data.reviewGenType}' isn't supported`);
      }),
    );
  }

  public getReviewVideoSetting(): Observable<string> {
    return this.ajaxService.get(ApiRoutesV1.reviewGenerationSettingsGet).pipe(
      map((response: HttpResponse<AjaxData>) => {
        return response.body.data.reviewGenVideoSetting;
      }),
    );
  }

  public getConfirmationMessages(): Observable<ConfirmationMessages> {
    return this.ajaxService.get(ApiRoutesV1.reviewGenerationSettingsGet).pipe(
      map((response: HttpResponse<AjaxData>) => {
        return {
          positiveReviewMessage: response.body.data.positiveReviewMessage,
          negativeReviewMessage: response.body.data.negativeReviewMessage,
        };
      }),
    );
  }

  public getDefaultConfirmationMessages(): Observable<ConfirmationMessages> {
    return this.ajaxService.get(ApiRoutesV1.confirmationMessageGetDefault).pipe(
      map((response: HttpResponse<AjaxData>) => {
        return {
          positiveReviewMessage: response.body.data.positiveReviewMessage,
          negativeReviewMessage: response.body.data.negativeReviewMessage,
        };
      }),
    );
  }

  public updateConfirmationMessages(confirmationMessages: ConfirmationMessages): Observable<HttpResponse<AjaxData>> {
    const httpParams: HttpParams = blankHttpParamsWithCustomEncoder
      .set('positiveReviewMessage', confirmationMessages.positiveReviewMessage)
      .set('negativeReviewMessage', confirmationMessages.negativeReviewMessage);
    return this.ajaxService.post(
      ApiRoutesV1.confirmationMessageUpdate,
      httpParams.toString(),
      formUrlEncodedHttpOptions,
    );
  }

  public getDefaultTemplate(): void {
    this.ajaxService.get(ApiRoutesV1.defaultTemplateIdGet).subscribe((response: HttpResponse<AjaxData>) => {
      this.defaultTemplateId$$.next(response.body.data);
    });
  }

  public updateDefaultTemplate(templateId: string): Observable<HttpResponse<AjaxData>> {
    const httpParams: HttpParams = blankHttpParamsWithCustomEncoder.set('templateId', templateId);
    this.defaultTemplateId$$.next(templateId);
    return this.ajaxService.post(ApiRoutesV1.defaultTemplateIdUpdate, httpParams.toString(), formUrlEncodedHttpOptions);
  }

  public get defaultTemplateId$(): Observable<string> {
    return this.defaultTemplateId$$.asObservable();
  }
}

export interface ConfirmationMessages {
  positiveReviewMessage: string;
  negativeReviewMessage: string;
}
